import { useRef, type KeyboardEvent } from 'react'
import { cx } from './cx'
import styles from './Segmented.module.css'

export interface SegmentedItem<T extends string> {
  id: T
  label: string
  count?: number
}

interface SegmentedProps<T extends string> {
  items: SegmentedItem<T>[]
  value: T
  onChange: (id: T) => void
  'aria-label'?: string
}

export function Segmented<T extends string>({
  items,
  value,
  onChange,
  'aria-label': ariaLabel,
}: SegmentedProps<T>) {
  const ref = useRef<HTMLDivElement>(null)

  // Roving tabindex with arrow keys, per the tabs pattern.
  function onKeyDown(e: KeyboardEvent) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    const i = items.findIndex((it) => it.id === value)
    const next = items[(i + (e.key === 'ArrowRight' ? 1 : items.length - 1)) % items.length]
    if (!next) return
    onChange(next.id)
    ref.current?.querySelector<HTMLElement>(`[data-id="${next.id}"]`)?.focus()
  }

  return (
    <div
      ref={ref}
      role="tablist"
      aria-label={ariaLabel}
      className={styles.track}
      onKeyDown={onKeyDown}
    >
      {items.map((it) => {
        const on = it.id === value
        return (
          <button
            key={it.id}
            type="button"
            role="tab"
            data-id={it.id}
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            className={cx(styles.item, on && styles.selected)}
            onClick={() => onChange(it.id)}
          >
            {it.label}
            {typeof it.count === 'number' && <span className={styles.count}>{it.count}</span>}
          </button>
        )
      })}
    </div>
  )
}
